import clsx from 'clsx';
import type { FC } from 'react';
import React, {
  useCallback,
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { createUseStyles, useTheme } from 'react-jss';
import { useHistory } from 'react-router';
import { WHITE } from '../../backend/helpers/Theme';
import mode from '../../shared/helpers/Mode';
import { LocaleContext } from '../contexts/LocaleContext';
import { SelectionContext } from '../contexts/SelectionContext';
import { SettingsContext } from '../contexts/SettingsContext';
import { MOBILE, ViewModeContext } from '../contexts/ViewModeContext';
import Languages from '../helpers/Languages';
import { darkenColor } from '../helpers/Theme';
import { URLtoCleanString } from '../helpers/Url';
import { EnhancedKioskLanguageDialog } from './EnhancedKioskLanguageDialog';
import { EnhancedKioskLanguagePicker } from './EnhancedKioskLanguagePicker';
import { Clock } from './icons/Clock';
import { TapIcon } from './KioskIcons';
import { KioskLandingChoices } from './KioskLandingChoices';
import { KioskQrCode } from './KioskQrCode';

export const FOOTER_HEIGHT = '7rem';
export const QR_FOOTER_HEIGHT = '8rem';
export const MOBILE_FOOTER_HEIGHT = '5.5rem';
export const IDLE_DURATION = 30000;

const getBackgroundColor = (theme, animate = false) => {
  if (theme.palette.contrastText === WHITE) {
    return animate ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)';
  }

  return animate ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.15)';
};

const getIconBackgroundColor = (theme) => {
  return theme.palette.contrastText === WHITE
    ? 'rgba(255, 255, 255, 0.2)'
    : 'rgba(0, 0, 0, 0.15)';
};

const useStyles = createUseStyles((theme: any) => ({
  bgDarkPrimary: {
    background: darkenColor(theme.palette.primary[400], 0.5),
  },
  bgPrimary: {
    background: theme.palette.primary[400],
  },
  collapsed: {
    paddingTop: '0px',
    maxHeight: '0px',
    opacity: 0,
  },
  divider: {
    margin: '1.5rem 0',
    '@media (min-width: 640px)': {
      margin: '2rem 0',
    },
    '@media (orientation: portrait) and (min-width: 640px)': {
      margin: '3rem 0 4rem 0',
    },
  },
  expanded: {
    paddingTop: '2rem',
    maxHeight: '100px',
    opacity: 1,
  },
  footerHeight: {
    height: ({ footerHeight }) => footerHeight,
  },
  icon: {
    backgroundColor: getIconBackgroundColor(theme),
  },
  contentHeight: {
    '@supports (height: 100dvh)': {
      height: ({ footerHeight }) => `calc(100dvh - ${footerHeight})`,
    },
    '@media not all and (height: 100dvh)': {
      height: ({ footerHeight }) => `calc(100vh - ${footerHeight})`,
    },
  },
  logo: {
    maxHeight: '3.5rem',
  },
  qrCode: {
    aspectRatio: '1 / 1',
    height: '100%',
  },
  fadeIn: {
    animation: '$zoomFadeIn 0.25s ease-out forwards',
    opacity: 0,
  },
  portraitPadding: {
    '@media (orientation: portrait) and (min-width: 640px)': {
      paddingTop: '2rem',
    },
  },
  pulseScale: {
    animation: '$pulseScale 1.5s infinite cubic-bezier(0.5,2.5,0,1)',
  },
  root: {
    color: theme.palette.contrastText,
    '-webkit-tap-highlight-color': 'transparent',
  },
  subtitle: {
    fontSize: '2rem',
    '@media (min-width: 640px)': {
      fontSize: '2.75rem',
    },
  },
  tapIcon: {
    display: 'inline-block',
    backgroundColor: getBackgroundColor(theme),
    animation: '$pulseColor 1.5s infinite ease-in-out',
  },
  transition: {
    transition: 'all 0.5s ease-in-out',
  },
  '@keyframes pulseScale': {
    '0%, 100%': { transform: 'scale(0.92)' },
    '50%': { transform: 'scale(1.175)' },
  },
  '@keyframes pulseColor': {
    '0%, 100%': { backgroundColor: getBackgroundColor(theme) },
    '50%': { backgroundColor: getBackgroundColor(theme, true) },
  },
  '@keyframes zoomFadeIn': {
    from: {
      opacity: 0,
      transform: 'scale(0.8)',
    },
    to: {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
}));

export const EnhancedKioskLanding: FC = () => {
  const history = useHistory();
  const viewMode = useContext(ViewModeContext);
  const isMobile = viewMode === MOBILE;
  const bookingThroughKioskQr = mode.isBookingThroughKioskQR();
  const [{ location }, setSelections] = useContext(SelectionContext);
  const [locale, setLocale] = useContext(LocaleContext);
  const [initialLocale] = useState(locale);
  const [isSelectingLanguage, setSelectingLanguage] = useState(false);
  const [idleTimer, setIdleTimer] = useState<NodeJS.Timeout | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [isIdleVisible, setIsIdleVisible] = useState(
    !isMobile && !bookingThroughKioskQr,
  );
  const [choicesClickable, setChoicesClickable] = useState(
    isMobile || bookingThroughKioskQr,
  );

  const {
    availableLanguages,
    kioskQrCode,
    kioskShowCustomMessage,
    kioskCustomMessage,
  } = useContext(SettingsContext);

  const classes = useStyles({
    theme: useTheme(),
    isMobile,
    footerHeight: isMobile
      ? MOBILE_FOOTER_HEIGHT
      : kioskQrCode
      ? QR_FOOTER_HEIGHT
      : FOOTER_HEIGHT,
  });

  const languagesLength = useMemo(() => {
    return isMobile ? 1 : kioskQrCode ? 2 : 4;
  }, [isMobile, kioskQrCode]);

  const showFooter = useMemo(() => {
    if ((isMobile || !kioskQrCode) && availableLanguages.length <= 1) {
      return false;
    }

    return true;
  }, [availableLanguages.length, isMobile, kioskQrCode]);

  const defaultLocationMessage = useMemo(() => {
    if (!location) {
      return null;
    }

    const today = new Date()
      .toLocaleDateString('en-US', {
        weekday: 'long',
      })
      .toLowerCase();

    const todaysClosingTime = location.hours[today].closed;

    if (!todaysClosingTime) {
      return null;
    }

    const [hour, minute] = todaysClosingTime.split(':').map(Number);
    const date = new Date();
    date.setHours(hour, minute, 0);

    const closing = date.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: '2-digit',
    });

    return (
      <FormattedMessage id="Landing.kiosk.open_until" values={{ closing }} />
    );
  }, [locale, location]);

  const resetTimer = useCallback(() => {
    if (idleTimer) {
      clearTimeout(idleTimer);
    }

    const newTimer = setTimeout(() => {
      setIsIdleVisible(true);
      setSelectingLanguage(false);

      Languages.setCurrent(initialLocale);
      setLocale(initialLocale);

      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set('lang', initialLocale);
      history.push({ search: URLtoCleanString(searchParams) });
      setSelections({
        language: initialLocale,
        search: URLtoCleanString(searchParams),
      });
    }, IDLE_DURATION);

    setIdleTimer(newTimer);
  }, [history, idleTimer, initialLocale, setLocale, setSelections]);

  useEffect(() => {
    if (isMobile || bookingThroughKioskQr) {
      return;
    }

    const handleAction = () => {
      setIsIdleVisible(false);
      resetTimer();
    };

    const element = contentRef.current;

    element?.addEventListener('mouseup', handleAction);
    element?.addEventListener('touchend', handleAction);

    return () => {
      element?.removeEventListener('mouseup', handleAction);
      element?.removeEventListener('touchend', handleAction);

      if (idleTimer) {
        clearTimeout(idleTimer);
      }
    };
  }, [idleTimer, isMobile, resetTimer]);

  useEffect(() => {
    if (!isIdleVisible) {
      const timer = setTimeout(() => {
        setChoicesClickable(true);
      }, 400);
      return () => clearTimeout(timer);
    } else {
      setChoicesClickable(false);
    }
  }, [isIdleVisible]);

  return (
    <div
      className={clsx(
        classes.bgDarkPrimary,
        classes.root,
        'flex flex-col justify-center items-center',
      )}
    >
      <main
        className={clsx(
          classes.bgPrimary,
          showFooter ? classes.contentHeight : 'h-screen',
          classes.transition,
          'relative w-full flex flex-col justify-center overflow-auto',
          showFooter && 'rounded-b-3xl',
        )}
      >
        <section
          className={clsx(
            classes.transition,
            'w-full min-h-full flex-1 flex flex-col overflow-auto',
          )}
          ref={contentRef}
        >
          <div className="min-h-full m-auto max-w-5xl md:max-w-4xl overflow-auto sm:overflow-visible px-4 sm:px-12">
            <div
              className={clsx(
                'text-center py-10 overflow-auto sm:overflow-visible ' +
                  'flex flex-col justify-center min-h-full w-full',
                classes.transition,
              )}
            >
              <Header />
              <div
                className={clsx(
                  classes.transition,
                  isIdleVisible ? 'pt-0' : classes.portraitPadding,
                  'flex flex-col gap-y-5 items-center justify-center',
                )}
              >
                <div
                  className={clsx(
                    'overflow-hidden',
                    classes.transition,
                    isIdleVisible ? classes.expanded : classes.collapsed,
                  )}
                >
                  <h1 className="text-5xl font-semibold mb-2" id="title">
                    <FormattedMessage id="Landing.kiosk.title" />
                  </h1>
                </div>
                <h2
                  className={clsx(
                    classes.subtitle,
                    classes.transition,
                    'font-medium px-6 sm:px-0',
                  )}
                >
                  <FormattedMessage id="Landing.kiosk.subtitle" />
                </h2>
                {kioskShowCustomMessage && kioskCustomMessage ? (
                  <h3
                    className={isMobile ? 'text-xl' : 'text-2xl'}
                    dangerouslySetInnerHTML={{ __html: kioskCustomMessage }}
                  />
                ) : defaultLocationMessage ? (
                  <div className="flex gap-4 items-center justify-center">
                    <Clock aria-hidden className="w-7 h-7 hidden sm:block" />
                    <h3 className={isMobile ? 'text-xl' : 'text-2xl'}>
                      {defaultLocationMessage}
                    </h3>
                  </div>
                ) : null}
              </div>
              <Divider />
              <div className={classes.transition}>
                <div
                  className={clsx(
                    classes.transition,
                    `overflow-hidden ${
                      isIdleVisible ? 'block opacity-100' : 'hidden opacity-0'
                    }`,
                  )}
                >
                  <div className={`${classes.tapIcon} p-6 mb-6 rounded-full`}>
                    <TapIcon className={classes.pulseScale} />
                  </div>
                  <p className="text-xl uppercase">
                    <FormattedMessage id="Landing.kiosk.touch" />
                  </p>
                </div>
                <div
                  className={clsx(
                    classes.transition,
                    `overflow-hidden ${
                      isIdleVisible ? 'hidden opacity-0' : 'block opacity-100'
                    }`,
                  )}
                >
                  <KioskLandingChoices
                    className={clsx(
                      choicesClickable
                        ? 'pointer-events-auto'
                        : 'pointer-events-none',
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        <EnhancedKioskLanguageDialog
          onDismiss={() => {
            setSelectingLanguage(false);
          }}
          open={isSelectingLanguage}
        />
      </main>
      {showFooter ? (
        <footer
          className={clsx(
            classes.bgDarkPrimary,
            classes.footerHeight,
            'w-full flex items-center py-3 px-7',
            languagesLength === 2 ? 'justify-between' : 'justify-center',
          )}
          data-testid="kiosk-footer"
        >
          <QrCode />
          <EnhancedKioskLanguagePicker
            maxLength={languagesLength}
            onOpen={() => {
              resetTimer();
              setSelectingLanguage(!isSelectingLanguage);
            }}
          />
        </footer>
      ) : null}
    </div>
  );
};

const Header: FC = () => {
  const classes = useStyles({ theme: useTheme() });

  const { logo } = (window as any).state.theme;
  const vendorName = (window as any).state.vendor_name;

  return (
    <div
      className={clsx(
        classes.bgPrimary,
        classes.transition,
        'w-full flex items-center sm:items-end justify-center',
      )}
    >
      <div className="bg-white-100 px-8 py-3 rounded-full max-w-66 sm:min-w-40 sm:max-w-xs sm:min-h-20 mx-12 flex items-center justify-center">
        {logo !== null ? (
          <img
            alt={vendorName}
            className={classes.logo}
            data-testid="logo"
            src={logo}
          />
        ) : (
          <h1 className="text-[2rem] sm:text-3xl leading-10 font-semibold text-black-90 text-center">
            {vendorName}
          </h1>
        )}
      </div>
    </div>
  );
};

const QrCode: FC = () => {
  const { kioskQrCode } = useContext(SettingsContext);
  const viewMode = useContext(ViewModeContext);
  const isMobile = viewMode === MOBILE;

  return kioskQrCode && !isMobile ? (
    <div className="relative h-full w-fit flex gap-4 items-center">
      <KioskQrCode />
      <div className="flex flex-col items-start gap-2 font-semibold">
        <span className="text-xl font-bold">
          <FormattedMessage id="Landing.kiosk.qr_code.scan" />
        </span>
        <span className="inline-block max-w-48 leading-5">
          <FormattedMessage id="Landing.kiosk.qr_code.manage" />
        </span>
      </div>
    </div>
  ) : null;
};

const Divider = () => {
  const classes = useStyles({ theme: useTheme() });

  return (
    <div>
      <hr
        className={clsx(
          classes.divider,
          classes.transition,
          'bg-current rounded-full h-0.5 w-full border-0 opacity-25',
        )}
      />
    </div>
  );
};
